import { useState, useMemo } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Play, PauseCircle, RotateCw } from 'lucide-react';
import { useQueryClient } from 'react-query';
import { Button } from '@/components/ui/button';
import axiosClient from '@/utils/axiosClient';
import Modal from '../Modals/Modal';
import { Separator } from '../ui/separator';

type Action = 'start' | 'pause' | 'resume';

interface RunCampaignModalProps {
  campaignId: string;
  action: Action;
}

const RunCampaignModal = NiceModal.create(({ campaignId, action }: RunCampaignModalProps) => {
  const queryClient = useQueryClient();
  const modal = useModal('run-campaign-modal');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { title, description, primaryLabel, primaryIcon } = useMemo(() => {
    switch (action) {
      case 'pause':
        return {
          title: 'Pause Campaign',
          description: 'Clicking below will pause the campaign. You can resume it at any time.',
          primaryLabel: isSubmitting ? 'Pausing...' : 'Pause Campaign',
          primaryIcon: <PauseCircle />,
        };
      case 'resume':
        return {
          title: 'Resume Campaign',
          description: 'Clicking below will resume the campaign and resume calling contacts.',
          primaryLabel: isSubmitting ? 'Resuming...' : 'Resume Campaign',
          primaryIcon: <RotateCw />,
        };
      case 'start':
      default:
        return {
          title: 'Start Campaign',
          description:
            'Once ready, click below to start the campaign and begin contacting leads. You can add contacts later as well.',
          primaryLabel: isSubmitting ? 'Starting...' : 'Start Campaign',
          primaryIcon: <Play />,
        };
    }
  }, [action, isSubmitting]);

  const handleAction = async () => {
    setIsSubmitting(true);
    try {
      const response = await axiosClient.post(`v1/campaigns/${campaignId}/${action}`);
      if (response.status !== 201) {
        queryClient.invalidateQueries('v1/campaigns');
        modal.hide();
      }
    } catch (error) {
      console.error(`Error ${action}ing campaign:`, error);
      // Optionally handle error UI or notifications
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal modal={modal} title={title}>
      <p className="mb-8">{description}</p>
      <Separator className="mb-8" />
      <div className="flex items-center justify-end gap-2 mt-4">
        <Button disabled={isSubmitting} onClick={handleAction} className="w-full sm:w-fit">
          {primaryIcon}
          {primaryLabel}
        </Button>
      </div>
    </Modal>
  );
});

export default RunCampaignModal;
